import { useState, useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function StepperRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()
  const children = element.children ?? []
  const initialStep = (element.options as any)?.activeStep ?? 0
  const [current, setCurrent] = useState<number>(initialStep)

  const handleStep = useCallback(
    (step: number) => {
      setCurrent(step)
      dispatchEvent(element.id, "change", { step })
    },
    [element.id, dispatchEvent],
  )

  return (
    <div
      className={cn("flex flex-col gap-4", element.className)}
      data-testid={`stepper-${element.id}`}
    >
      {/* Step indicator row */}
      <div className="flex flex-row items-center gap-0">
        {children.map((child, i) => (
          <div key={child.id} className="flex flex-row items-center flex-1">
            <button
              type="button"
              className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-none cursor-pointer transition-colors",
                i < current
                  ? "bg-primary text-primary-foreground"
                  : i === current
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
              )}
              onClick={() => handleStep(i)}
              aria-label={`Go to step ${i + 1}`}
            >
              {i < current ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                i + 1
              )}
            </button>
            {i < children.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-px mx-2",
                  i < current ? "bg-primary" : "bg-border",
                )}
              />
            )}
          </div>
        ))}
      </div>
      {/* Active step content */}
      {current < children.length && (
        <ElementRenderer element={children[current]} />
      )}
    </div>
  )
}
